'use client'
import React, { FC, ReactElement, useRef } from 'react'

import useEventListener from '@/utils/hooks/useEventListener'
import ArrowRightIcon from '@/icons/arrow-right'
import ArrowLeftIcon from '@/icons/arrow-left'
import InfiniteScroll from 'react-infinite-scroll-component'

import styles from './CardScroller.module.css'

const LIMIT = 30

const _CardItem = ({
	image: _image,
	link: _link,
	title: _title
}: {
	image?: string
	link?: string
	title?: string
}) => {
	return (
		<div className="inline-block px-3">
			<div className="size-64 max-w-xs overflow-hidden rounded-lg shadow-md bg-white hover:shadow-xl transition-shadow duration-300 ease-in-out"></div>
		</div>
	)
}

const CardScroller: FC<{ children?: any; items?: any[]; renderItem?: any }> = (
	props
): ReactElement => {
	const { children, items, renderItem } = props
	const scrollContainerRef = useRef<HTMLDivElement>(null)
	const [showRight, setShowRight] = React.useState(true)
	const [showLeft, setShowLeft] = React.useState(false)

	React.useEffect(() => {
		if (scrollContainerRef && scrollContainerRef.current) {
			const scrollWidth = scrollContainerRef.current.scrollWidth
			const scrollLeft = scrollContainerRef.current.scrollLeft
			const viewWidth = scrollContainerRef.current.getClientRects()[0].width

			const isRight = scrollWidth <= scrollLeft + viewWidth

			setShowRight(!isRight)
			setShowLeft(scrollLeft > 30)
		}
	}, [scrollContainerRef])

	const [page, setPage] = React.useState(0)
	const fetchMore = React.useCallback(async () => setPage((prev) => prev + 1), [])
	const viewItems = React.useMemo(() => {
		if (!items || !Array.isArray(items)) {
			return []
		}

		return (items || [])?.slice(0, (1 + page) * LIMIT)
	}, [page, items])
	const [key, _setKey] = React.useState(() => Math.random().toString().slice(1, 5))

	useEventListener(
		'scroll',
		(_evt) => {
			if (!scrollContainerRef.current) return
			const scrollWidth = scrollContainerRef.current.scrollWidth
			const scrollLeft = scrollContainerRef.current.scrollLeft
			const viewWidth = scrollContainerRef.current.getClientRects()[0].width

			const isRight = scrollWidth <= scrollLeft + viewWidth
			const isRightIsh = scrollWidth <= scrollLeft + viewWidth + 200

			if (isRightIsh) {
				fetchMore()
			}

			setShowRight(!isRight)
			setShowLeft(scrollLeft > 30)
		},
		scrollContainerRef as any
	)

	const handleScroll = (direction: 'left' | 'right') => {
		if (!scrollContainerRef.current) return
		const viewWidth = scrollContainerRef.current.getClientRects()[0].width
		const distance = viewWidth
		scrollContainerRef.current.scrollBy({
			top: 0,
			left: direction === 'left' ? -distance : distance,
			behavior: 'smooth'
		})
	}

	return (
		<div className="flex flex-col m-auto p-auto relative">
			{showLeft && (
				<div className="z-10 absolute -left-2 xl:-left-5 top-0 h-full flex flex-col justify-center">
					<button
						onClick={() => handleScroll('left')}
						className={`${styles.button} hidden md:flex items-center justify-center rounded-full border border-border bg-card size-10 mb-11`}
					>
						<ArrowLeftIcon className="size-4" />
					</button>
				</div>
			)}
			{showRight && (
				<div className="absolute -right-2 xl:-right-5 top-0 h-full flex flex-col justify-center z-10">
					<button
						onClick={() => handleScroll('right')}
						className={`${styles.button} hidden md:flex items-center justify-center rounded-full border border-border bg-card size-10 mb-11`}
					>
						<ArrowRightIcon className="size-4" />
					</button>
				</div>
			)}
			<div
				ref={scrollContainerRef}
				id={key}
				className="flex w-full overflow-x-scroll hide-scroll-bar"
			>
				<div className="flex flex-nowrap gap-4">
					{children}
					{!!viewItems?.length && (
						<InfiniteScroll
							className={styles.infiniteScroll}
							dataLength={viewItems.length}
							style={{ display: 'flex', flexDirection: 'row', gap: '16px' }}
							next={() => {}}
							hasMore={viewItems.length % LIMIT === 0}
							scrollableTarget={key}
							loader={<React.Fragment />}
						>
							{(viewItems || [])?.map(renderItem)}
						</InfiniteScroll>
					)}
				</div>
			</div>
		</div>
	)
}

export default CardScroller
